import clsx from "clsx";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { useField } from "react-aria";
import {
  DialogTrigger,
  Autocomplete,
  useFilter,
  ButtonContext,
  type Selection,
} from "react-aria-components";

import { setInteractionModality } from "@react-aria/interactions";

import { Text } from "../../Text";
import styles from "./styles.module.css";
import { ListBox } from "../../ListBox";
import {
  Popover,
  POPOVER_LIST_BOX_MAX_HEIGHT,
  useRootContainer,
} from "../../Popover";
import { selectStyles } from "../../Select";
import { TextField } from "../../TextField";
import { FieldLabel } from "../../FieldLabel";
import { textInputStyles } from "../../Input";
import { inputFieldStyles } from "../../Field";
import type { MultiSelectProps } from "./types";
import { fieldErrorStyles } from "../../FieldError";

import { MultiSelectValue } from "./MultiSelectValue";
import { Checkbox } from "../../Checkbox";
import { EmptyState } from "../../EmptyState";
import { ListBoxItem } from "../../ListBoxItem";

/**
 * Note: React aria components does not provide us any mutliselect componennt or hooks for it.
 * We are just replicating the behaviour of mutli select component with all available hooks and components.
 * Few things are implemented manually like opening the popover on keydown or keyup when the button is focused
 * or focusing the trigger on click of label.
 *
 * This is a temporary solution until we have a mutli select component from react aria components library.
 */
export const MultiSelect = <T extends { label: string; value: string }>(
  props: MultiSelectProps<T>,
) => {
  const {
    allowSelectAll = false,
    contextualHelp,
    defaultSelectedKeys = new Set(),
    disabledKeys,
    emptyStateTitle,
    errorMessage,
    excludeFromTabOrder,
    filter: filterProp,
    isDisabled,
    isInvalid,
    isLoading,
    isRequired,
    items,
    label,
    onInputChange,
    onOpenChange,
    onSelectionChange: onSelectionChangeProp,
    placeholder,
    selectAllLabel = "Select all",
    selectedKeys: selectedKeysProp,
    shouldResetInputOnClose = true,
    size,
  } = props;
  const root = useRootContainer();
  const [_selectedKeys, _setSelectedKeys] = useState<Selection>();
  const selectedKeys = selectedKeysProp ?? _selectedKeys ?? defaultSelectedKeys;
  const setSelectedKeys = onSelectionChangeProp ?? _setSelectedKeys;
  const { labelProps } = useField(props);
  const { contains } = useFilter({ sensitivity: "base" });
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Note we have to use controlled state for the popover as we need a custom logic to open the popover
  // for the usecase where we need to open the popover on keydown or keyup when the button is focused.
  const [isOpen, setOpen] = useState(false);
  // The search input is controlled here (not left to Autocomplete) so that
  // the select-all row can compute the VISIBLE (filtered) options, observers
  // can be notified via onInputChange, and reset-on-close stays configurable.
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      onInputChange?.(value);
    },
    [onInputChange],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open === isOpen) return;

      setOpen(open);
      onOpenChange?.(open);

      // Preserves the pre-controlled-input behavior where the (unmounting)
      // popover dropped the search text on close. Callers doing server-side
      // filtering pass shouldResetInputOnClose={false} to keep it.
      if (!open && shouldResetInputOnClose && inputValue !== "") {
        setInputValue("");
        onInputChange?.("");
      }
    },
    [isOpen, onOpenChange, shouldResetInputOnClose, inputValue, onInputChange],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      handleOpenChange(true);
    }
  };

  const filter =
    filterProp ??
    ((textValue: string, inputValue: string) =>
      contains(textValue, inputValue));

  const itemsArray = Array.from(items ?? []) as T[];
  const visibleItems =
    inputValue === ""
      ? itemsArray
      : itemsArray.filter((item) => filter(item.label, inputValue));
  const selectedKeySet =
    selectedKeys === "all"
      ? new Set(itemsArray.map((item) => item.value))
      : new Set([...selectedKeys]);
  const isAllVisibleSelected =
    visibleItems.length > 0 &&
    visibleItems.every((item) => selectedKeySet.has(item.value));

  // Loading beats empty (M3 async contract): no empty state while options
  // are loading. Consistent with Select/ComboBox, the empty state renders
  // ONLY when the consumer supplies copy — widgets own the strings
  // (createMessage at the widget layer); the DS layer hard-codes none.
  // Memoized so the ListBox does not receive a fresh function every render.
  const renderEmptyState = useMemo(() => {
    if (!Boolean(emptyStateTitle) || Boolean(isLoading)) return undefined;

    return function ListBoxEmptyState() {
      return <EmptyState title={emptyStateTitle as string} />;
    };
  }, [emptyStateTitle, isLoading]);

  const handleSelectAllChange = () => {
    // Matches the classic multi-select semantics: checking selects the union
    // of the current selection and the visible options; unchecking clears the
    // WHOLE selection (not just the visible part).
    if (isAllVisibleSelected) {
      setSelectedKeys(new Set());

      return;
    }

    const nextKeys = new Set(selectedKeySet);

    visibleItems.forEach((item) => nextKeys.add(item.value));
    setSelectedKeys(nextKeys);
  };

  return (
    <ButtonContext.Provider value={{ onKeyDown, ref: triggerRef }}>
      <div className={inputFieldStyles.field}>
        {Boolean(label) && (
          <FieldLabel
            {...labelProps}
            contextualHelp={contextualHelp}
            isDisabled={isDisabled}
            isRequired={isRequired}
            // this is required to imitate the behavior where on click of label, the trigger or input is focused.
            // In our select component,  this is done by the useSelect hook. Since we don't have that for multi select,
            // we are doing this manually here
            onClick={() => {
              if (triggerRef.current) {
                triggerRef.current.focus();

                setInteractionModality("keyboard");
              }
            }}
          >
            {label}
          </FieldLabel>
        )}
        <div
          className={clsx(
            textInputStyles.inputGroup,
            selectStyles.selectInputGroup,
          )}
        >
          <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
            <MultiSelectValue
              excludeFromTabOrder={excludeFromTabOrder}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              isLoading={isLoading}
              items={items}
              placeholder={placeholder}
              selectedKeys={selectedKeys}
              size={size}
              triggerRef={triggerRef}
            />
            <Popover
              UNSTABLE_portalContainer={root}
              className={styles.popover}
              maxHeight={POPOVER_LIST_BOX_MAX_HEIGHT}
              placement="bottom start"
              style={
                {
                  "--trigger-width": `${triggerRef?.current?.offsetWidth}px`,
                } as React.CSSProperties
              }
              triggerRef={triggerRef}
            >
              <Autocomplete
                filter={filter}
                inputValue={inputValue}
                onInputChange={handleInputChange}
              >
                <TextField autoFocus className={styles.textField} />
                {allowSelectAll && visibleItems.length > 0 && (
                  <Checkbox
                    className={styles.selectAllItem}
                    isSelected={isAllVisibleSelected}
                    onChange={handleSelectAllChange}
                  >
                    {selectAllLabel}
                  </Checkbox>
                )}
                <ListBox
                  className={styles.listBox}
                  disabledKeys={disabledKeys}
                  items={items}
                  onSelectionChange={setSelectedKeys}
                  renderEmptyState={renderEmptyState}
                  selectedKeys={selectedKeys}
                  selectionMode="multiple"
                  shouldFocusWrap
                >
                  {(item: T) => (
                    <ListBoxItem
                      className={styles.listBoxItem}
                      id={item.value}
                      textValue={item.label}
                    >
                      {({ isSelected }) => (
                        <>
                          <Checkbox
                            className={styles.listBoxItemCheckbox}
                            isSelected={isSelected}
                          />
                          {item.label}
                        </>
                      )}
                    </ListBoxItem>
                  )}
                </ListBox>
              </Autocomplete>
            </Popover>
          </DialogTrigger>
        </div>
        {/* We can't use our FieldError component as it only works when used with FieldErrorContext.
          We can use it in our Select and other inputs because the implementation is abstracted in the react aria components library.
          But since for MultiSelect, we don't have any component from react-aria, we have to manually render the error message here. */}
        <div className={fieldErrorStyles.errorText}>
          <Text color="negative" size="caption">
            {errorMessage}
          </Text>
        </div>
      </div>
    </ButtonContext.Provider>
  );
};
